// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
/**
 * Session-store adapter — platform-aware session-token persistence.
 *
 * Threat model:
 *   - Web:      The better-auth session token lives in an HttpOnly cookie.
 *               JavaScript cannot read or write it; the adapter is a no-op
 *               for the token but still exposes `clear()` so the unified
 *               logout path works.
 *   - Native:   The Capacitor WebView's cookie jar is reachable on a
 *               jail-broken device. Persist the session token in
 *               `@capacitor/preferences` which is backed by Keychain on iOS
 *               and EncryptedSharedPreferences / Android Keystore on
 *               Android. NEVER fall through to `localStorage`.
 *
 * Anything derived from the session that the runtime needs (auth headers,
 * scoped device key, etc.) is held in a module-private variable here so it
 * is reachable from the request wrapper without being attached to `window`.
 */

import { Preferences } from "@capacitor/preferences";
import { native } from "@/native";

/** Storage key used by NativeSessionStore. The audit script allowlists this name. */
export const NATIVE_SESSION_TOKEN_KEY = "auth_session_token";

/** One-shot migration marker. */
export const NATIVE_MIGRATION_KEY = "auth_migrated_to_native_v1";

/** Legacy LTR localStorage keys that may have been written by an older PWA/TWA build. */
const LEGACY_LOCAL_STORAGE_KEYS = [
  "auth_session_token",
  "better-auth.session_token",
  "session_token",
] as const;

/** Sensitive legacy keys that must be deleted, never migrated. */
const LEGACY_SENSITIVE_KEYS = [
  "master_password",
  "vault_master_key",
  "vault_derived_key",
  "vault_key",
  "derived_key",
] as const;

export interface SessionStore {
  /** Returns the session token if present (native), or `null` (web cookie-only). */
  getSessionToken(): Promise<string | null>;
  /** Persist or clear the session token. On web this is a no-op. */
  setSessionToken(token: string | null): Promise<void>;
  /** Wipe all adapter-owned session state. */
  clear(): Promise<void>;
}

/* ─────────────────────────────────────────────────────────────────── */
/*  Web adapter — HttpOnly cookie owns the session, JS does not.       */
/* ─────────────────────────────────────────────────────────────────── */

export class WebSessionStore implements SessionStore {
  async getSessionToken(): Promise<string | null> {
    return null;
  }

  async setSessionToken(_token: string | null): Promise<void> {
    // The browser cookie jar owns the session. Storing it in JS-reachable
    // memory or localStorage would defeat HttpOnly. No-op.
    void _token;
  }

  async clear(): Promise<void> {
    // Cookie eviction is handled by the better-auth signOut() flow that
    // calls into us; nothing JS-reachable to wipe here.
  }
}

/* ─────────────────────────────────────────────────────────────────── */
/*  Native adapter — Preferences-backed; access context in memory.     */
/* ─────────────────────────────────────────────────────────────────── */

/**
 * Module-private in-memory access context. Intentionally not attached to
 * `window` / `globalThis` so it cannot be read from the DevTools console
 * or by other modules through a global side door.
 */
let _accessContext: { authHeader?: string } = {};

export function getAccessContext(): Readonly<{ authHeader?: string }> {
  return _accessContext;
}

export function setAccessContextAuthHeader(value: string | undefined): void {
  _accessContext = { ..._accessContext, authHeader: value };
}

export function resetAccessContextForTests(): void {
  _accessContext = {};
}

export class NativeSessionStore implements SessionStore {
  async getSessionToken(): Promise<string | null> {
    const { value } = await Preferences.get({ key: NATIVE_SESSION_TOKEN_KEY });
    return value ?? null;
  }

  async setSessionToken(token: string | null): Promise<void> {
    if (token === null || token === "") {
      await Preferences.remove({ key: NATIVE_SESSION_TOKEN_KEY });
      setAccessContextAuthHeader(undefined);
      return;
    }
    // Single allowlisted call site that may persist a session-shaped string.
    await Preferences.set({ key: NATIVE_SESSION_TOKEN_KEY, value: token });
    setAccessContextAuthHeader(`Bearer ${token}`);
  }

  async clear(): Promise<void> {
    await Preferences.remove({ key: NATIVE_SESSION_TOKEN_KEY });
    setAccessContextAuthHeader(undefined);
  }
}

/* ─────────────────────────────────────────────────────────────────── */
/*  Factory + first-launch migration.                                  */
/* ─────────────────────────────────────────────────────────────────── */

function createSessionStore(): SessionStore {
  return native.isNative ? new NativeSessionStore() : new WebSessionStore();
}

/** The active store. Importers should hold this reference. */
export const sessionStore: SessionStore = createSessionStore();

/**
 * One-shot migration from a prior WebView/PWA/TWA build that may have
 * dropped the session token into `localStorage`.
 *
 * Idempotent — guarded by NATIVE_MIGRATION_KEY in `Preferences`.
 * Sensitive keys (master password, derived keys) are *deleted*, never
 * migrated, and surfaced via the returned report so the audit script can
 * flag them.
 */
export interface MigrationReport {
  ran: boolean;
  migratedSessionTokenFromKey: string | null;
  deletedSensitiveKeys: string[];
}

export async function migrateLegacyNativeStorageOnce(
  store: SessionStore = sessionStore,
): Promise<MigrationReport> {
  const report: MigrationReport = {
    ran: false,
    migratedSessionTokenFromKey: null,
    deletedSensitiveKeys: [],
  };

  if (!native.isNative) return report;

  const { value: marker } = await Preferences.get({ key: NATIVE_MIGRATION_KEY });
  if (marker === "true") return report;

  report.ran = true;

  // Always delete sensitive keys that should never have been persisted.
  for (const key of LEGACY_SENSITIVE_KEYS) {
    try {
      const v =
        typeof window !== "undefined" ? window.localStorage?.getItem(key) : null;
      if (v) {
        report.deletedSensitiveKeys.push(key);
        window.localStorage.removeItem(key);
      }
    } catch {
      // localStorage may be unavailable inside some WebViews — ignore.
    }
  }

  // Migrate the session token only if the native store does not already
  // hold one (never overwrite a real token).
  const existing = await store.getSessionToken();
  if (!existing) {
    for (const key of LEGACY_LOCAL_STORAGE_KEYS) {
      try {
        const candidate =
          typeof window !== "undefined" ? window.localStorage?.getItem(key) : null;
        if (candidate && candidate.length > 0) {
          await store.setSessionToken(candidate);
          report.migratedSessionTokenFromKey = key;
          window.localStorage.removeItem(key);
          break;
        }
      } catch {
        // ignore
      }
    }
  }

  // Always wipe legacy locations once we've considered them.
  for (const key of LEGACY_LOCAL_STORAGE_KEYS) {
    try {
      window.localStorage?.removeItem(key);
    } catch {
      // ignore
    }
  }

  await Preferences.set({ key: NATIVE_MIGRATION_KEY, value: "true" });
  return report;
}

/**
 * Lightweight diagnostic — intentionally does NOT return the token, only
 * whether one is present. Useful for cold-start gating without leaking
 * the value into any logger.
 */
export async function hasPersistedNativeSession(): Promise<boolean> {
  if (!native.isNative) return false;
  const tok = await sessionStore.getSessionToken();
  return typeof tok === "string" && tok.length > 0;
}
